import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/** How long the belt waits, after the last touch or user scroll, before it starts moving again. */
const TOUCH_RESUME_DELAY = 2500;

/** Frame delta cap, so coming back from a background tab never jumps the belt forward. */
const MAX_FRAME_DELTA = 100;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);

    window.addEventListener('resize', this.#handleResize);
    // Hover slow-down is a mouse-only nicety: touch pointers never "leave" reliably and the theme
    // editor's overlays can swallow pointerleave, which would leave the belt frozen at speed 0.
    if (!window.Shopify?.designMode) {
      this.addEventListener('pointerenter', this.#handlePointerEnter);
      this.addEventListener('pointerleave', this.#speedUp);
      this.addEventListener('pointercancel', this.#speedUp);
    }
    document.addEventListener('visibilitychange', this.#handleVisibility);

    // Opt-in: on touch (or a narrow window) the belt becomes a real horizontal scroller the shopper
    // can push with a finger, instead of a transform they can only watch. Only components that ask
    // for it with `data-touch-scroll` change; the marquee section keeps the animation everywhere.
    if (this.hasAttribute('data-touch-scroll')) {
      this.#touchScrollQuery = window.matchMedia('(max-width: 749px), (hover: none)');
      this.#reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.#touchScrollQuery.addEventListener('change', this.#syncScrollMode);
      this.#reducedMotionQuery.addEventListener('change', this.#syncScrollMode);
      this.#syncScrollMode();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('pointercancel', this.#speedUp);
    document.removeEventListener('visibilitychange', this.#handleVisibility);
    this.#touchScrollQuery?.removeEventListener('change', this.#syncScrollMode);
    this.#reducedMotionQuery?.removeEventListener('change', this.#syncScrollMode);
    this.#disableScrollMode();
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#slowDown();
  };

  /** A belt slowed down before the tab was hidden must never come back frozen. */
  #handleVisibility = () => {
    if (document.visibilityState === 'visible') this.#speedUp();
  };

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  /**
   * Milliseconds a `data-hover-stop-duration` belt takes to come to a stop under the pointer.
   * When set, the half-second wait before the ramp is skipped too: the default behaviour takes about
   * a second to settle, which reads as "hovering does nothing" on a belt of readable content.
   * @returns {number | null}
   */
  get #hoverStopDuration() {
    const value = Number(this.getAttribute('data-hover-stop-duration'));
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  /**
   * Ramps the belt's playback rate to `to` over `duration`, replacing any ramp in flight.
   * @param {number} to
   * @param {number} duration
   */
  #rampPlaybackRate(to, duration) {
    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    const from = this.#animation?.current ?? animation.playbackRate;
    this.#animation?.cancel();

    this.#animation = animateValue({
      duration,
      from,
      to,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        if (to === 1) {
          // `updatePlaybackRate` is asynchronous; settle on exactly 1 so the belt cannot stay slowed.
          animation.playbackRate = 1;
          if (animation.playState === 'paused') animation.play();
        }
        this.#animation = null;
      },
    });
  }

  #slowDownDebounced = debounce(() => {
    if (this.#animation) return;
    this.#rampPlaybackRate(0, ANIMATION_OPTIONS.duration);
  }, ANIMATION_OPTIONS.duration);

  #slowDown = () => {
    const immediate = this.#hoverStopDuration;

    if (immediate) {
      this.#slowDownDebounced.cancel();
      this.#rampPlaybackRate(0, immediate);
      return;
    }

    this.#slowDownDebounced();
  };

  #speedUp = () => {
    this.#slowDownDebounced.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;
    if (animation.playbackRate === 1 && !this.#animation) return;

    this.#rampPlaybackRate(1, this.#hoverStopDuration ?? ANIMATION_OPTIONS.duration);
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      // If the observer never reports (element not rendered yet, background tab), measure directly
      // after a moment so the belt always gets a speed and starts moving.
      const fallback = setTimeout(() => {
        intersectionObserver.disconnect();
        const marqueeWidth = this.getBoundingClientRect().width;
        const marqueeItemsWidth = marqueeItems[0]?.getBoundingClientRect().width ?? 0;
        const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
        this.#marqueeWidth = marqueeWidth;
        resolve({
          numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
          isHorizontalResize,
        });
      }, 1500);

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          clearTimeout(fallback);
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    // (upstream destructured a non-existent `newNumberOfCopies`, which set the speed to NaN and
    // froze the belt after any horizontal resize)
    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();

    if (this.#scrollMode) {
      this.#measureScroll();
      this.#setScrollLeft(this.scrollLeft);
    }
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }

  /* ---------------------------------------------------------------------------------------------
   * Touch scrolling (`data-touch-scroll`)
   *
   * On touch the belt stops being a transform the shopper can only watch and becomes a real
   * horizontal scroller: the finger pushes it, the browser gives momentum and rubber-banding for
   * free, and the auto-advance is driven by `scrollLeft` at exactly the speed the CSS animation
   * would have used. Because the component already renders the content twice, jumping back by one
   * copy's width when the scroll passes it is invisible, which is what makes the loop endless in
   * both directions.
   * ------------------------------------------------------------------------------------------- */

  /** @type {MediaQueryList | null} */
  #touchScrollQuery = null;

  /** @type {MediaQueryList | null} */
  #reducedMotionQuery = null;

  #scrollMode = false;

  /** @type {number | null} */
  #scrollFrame = null;

  /** @type {number | null} */
  #resumeTimer = null;

  #scrollLastFrame = 0;

  /** Pixels per second, derived from the belt's own animation duration. */
  #scrollSpeed = 0;

  /** Width of one content copy plus one gap — the distance after which the belt repeats itself. */
  #loopLength = 0;

  #scrollPaused = false;

  #pointerHeld = false;

  /** Set right before this component writes `scrollLeft`, so its own writes are not read as the shopper scrolling. */
  #selfScroll = false;

  #syncScrollMode = () => {
    const shouldScroll = Boolean(this.#touchScrollQuery?.matches) && !this.#reducedMotionQuery?.matches;

    if (shouldScroll) this.#enableScrollMode();
    else this.#disableScrollMode();
  };

  /**
   * Reads the belt's effective animation duration in seconds. The stylesheet applies its own factor
   * on mobile, so this has to come from the computed style rather than from `--marquee-speed` — and
   * the attribute that switches the animation off has to be lifted for the read. No frame is painted
   * in between, so the belt does not flicker.
   */
  #readAnimationDuration() {
    const hadAttribute = this.hasAttribute('data-scroll-mode');
    if (hadAttribute) this.removeAttribute('data-scroll-mode');

    const duration = parseFloat(getComputedStyle(this.refs.wrapper).animationDuration) || 0;

    if (hadAttribute) this.setAttribute('data-scroll-mode', '');

    return duration;
  }

  #measureScroll() {
    const { content, wrapper } = this.refs;
    const gap = parseFloat(getComputedStyle(wrapper).columnGap) || 0;
    const duration = this.#readAnimationDuration();

    this.#loopLength = content.getBoundingClientRect().width + gap;
    this.#scrollSpeed = duration > 0 && this.#loopLength > 0 ? this.#loopLength / duration : 0;
  }

  #enableScrollMode() {
    if (this.#scrollMode) return;

    this.#scrollMode = true;
    this.#measureScroll();
    this.setAttribute('data-scroll-mode', '');

    // A scrollable region has to be reachable and operable with a keyboard (WCAG 2.1.1).
    this.setAttribute('tabindex', '0');
    const label = this.getAttribute('data-scroll-label');
    if (label) {
      this.setAttribute('role', 'group');
      this.setAttribute('aria-label', label);
    }

    this.addEventListener('scroll', this.#handleScroll, { passive: true });
    this.addEventListener('pointerdown', this.#handlePointerHold);
    this.addEventListener('pointerup', this.#handlePointerRelease);
    this.addEventListener('pointercancel', this.#handlePointerRelease);

    this.#intersectionObserver.observe(this);
    this.#scrollLastFrame = 0;
    this.#scrollFrame = requestAnimationFrame(this.#scrollStep);
  }

  #disableScrollMode() {
    if (!this.#scrollMode) return;

    this.#scrollMode = false;
    if (this.#scrollFrame !== null) cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = null;
    if (this.#resumeTimer !== null) clearTimeout(this.#resumeTimer);
    this.#resumeTimer = null;

    this.removeEventListener('scroll', this.#handleScroll);
    this.removeEventListener('pointerdown', this.#handlePointerHold);
    this.removeEventListener('pointerup', this.#handlePointerRelease);
    this.removeEventListener('pointercancel', this.#handlePointerRelease);
    this.#intersectionObserver.disconnect();

    this.removeAttribute('data-scroll-mode');
    this.removeAttribute('tabindex');
    if (this.getAttribute('role') === 'group') {
      this.removeAttribute('role');
      this.removeAttribute('aria-label');
    }
    this.scrollLeft = 0;
    this.#scrollPaused = false;
    this.#pointerHeld = false;
  }

  /** Pauses while the belt is off screen: a belt nobody is looking at should not drift. */
  #intersectionObserver = new IntersectionObserver((entries) => {
    const entry = entries[0];
    if (entry) this.#offScreen = !entry.isIntersecting;
  });

  #offScreen = false;

  /**
   * @param {number} value
   * @returns {number}
   */
  #wrapScroll(value) {
    const length = this.#loopLength;
    if (!length) return value;
    if (value >= length) return value - length;
    if (value < 0) return value + length;
    return value;
  }

  /** @param {number} value */
  #setScrollLeft(value) {
    this.#selfScroll = true;
    this.scrollLeft = this.#wrapScroll(value);
  }

  /** @param {number} now */
  #scrollStep = (now) => {
    this.#scrollFrame = requestAnimationFrame(this.#scrollStep);

    const delta = this.#scrollLastFrame ? Math.min(now - this.#scrollLastFrame, MAX_FRAME_DELTA) : 0;
    this.#scrollLastFrame = now;

    if (this.#scrollPaused || this.#pointerHeld || this.#offScreen || !this.#scrollSpeed || !delta) return;

    this.#setScrollLeft(this.scrollLeft + (this.#scrollSpeed * delta) / 1000);
  };

  #handleScroll = () => {
    if (this.#selfScroll) {
      this.#selfScroll = false;
      return;
    }

    // The shopper is pushing the belt (finger, momentum after it, trackpad): hold off, and keep
    // holding off for as long as the scrolling continues.
    this.#pauseUntilIdle();

    const wrapped = this.#wrapScroll(this.scrollLeft);
    if (wrapped !== this.scrollLeft) {
      this.#selfScroll = true;
      this.scrollLeft = wrapped;
    }
  };

  #pauseUntilIdle() {
    this.#scrollPaused = true;
    if (this.#resumeTimer !== null) clearTimeout(this.#resumeTimer);
    if (this.#pointerHeld) return;

    this.#resumeTimer = setTimeout(() => {
      this.#scrollPaused = false;
      this.#resumeTimer = null;
    }, TOUCH_RESUME_DELAY);
  }

  #handlePointerHold = () => {
    this.#pointerHeld = true;
    this.#scrollPaused = true;
    if (this.#resumeTimer !== null) clearTimeout(this.#resumeTimer);
    this.#resumeTimer = null;
  };

  #handlePointerRelease = () => {
    this.#pointerHeld = false;
    this.#pauseUntilIdle();
  };
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
